import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import { AccordionDetails, Box, Stack, Typography, alpha } from "@mui/material";
import { PageNavigationPlugin } from "@react-pdf-viewer/page-navigation";
import { CommentForm } from "../scope-clauses/comments/CommentForm";
import { CommentsList } from "../scope-clauses/comments/CommentsList";
import { FeedbackChip } from "../scope-clauses/FeedbackChip";
import { ScopeClauseGuidance } from "../scope-clauses/ScopeClauseGuidance";
import { IGroupedV2Clause } from "./types";

interface V2ClauseAccordionDetailsProps {
    clauseGroup: IGroupedV2Clause;
    panelId: string;
    expanded: boolean;
    pageNavigationPluginInstance: PageNavigationPlugin;
    highlightedScopeClauseId?: number;
    onClauseSelect?: (scopeClauseId: number | null) => void;
}

export const V2ClauseAccordionDetails = ({
    clauseGroup,
    panelId,
    expanded,
    pageNavigationPluginInstance,
    highlightedScopeClauseId,
    onClauseSelect,
}: V2ClauseAccordionDetailsProps) => {
    const primaryScopeClause = clauseGroup.scopeClauses[0];

    return (
        <AccordionDetails
            sx={{
                bgcolor: "#fafbfc",
                borderTop: "1px solid #e2e8f0",
                p: 0,
            }}
            id={`${panelId}-content`}
        >
            <Stack>
                {/* Guidance section */}
                <Box sx={{ p: 2, pb: 1.5 }}>
                    <ScopeClauseGuidance scopeClause={primaryScopeClause} />
                </Box>

                {/* Comments section */}
                <Box
                    sx={{
                        bgcolor: "background.paper",
                        borderTop: "1px solid #f1f5f9",
                        borderBottom: "1px solid #f1f5f9",
                        px: 2,
                        py: 1.5,
                    }}
                >
                    <Stack gap={1.5}>
                        <CommentForm scopeClause={primaryScopeClause} />
                        <CommentsList
                            scopeClause={primaryScopeClause}
                            expanded={expanded}
                        />
                    </Stack>
                </Box>

                {/* Locations with feedback chips */}
                <Box sx={{ p: 2, pt: 1.5 }}>
                    <Stack gap={1}>
                        <Stack direction="row" alignItems="center" gap={0.5}>
                            <PlaceOutlinedIcon
                                sx={{
                                    fontSize: 14,
                                    color: alpha("#223876", 0.5),
                                }}
                            />
                            <Typography
                                sx={{
                                    fontSize: "0.7rem",
                                    fontWeight: 600,
                                    color: "#64748b",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.04em",
                                }}
                            >
                                Locations
                            </Typography>
                        </Stack>
                        <Stack direction="row" gap={0.75} flexWrap="wrap">
                            {clauseGroup.scopeClauses.map((scopeClause) => (
                                <FeedbackChip
                                    key={scopeClause.id}
                                    scopeClause={scopeClause}
                                    pageNavigationPluginInstance={
                                        pageNavigationPluginInstance
                                    }
                                    expanded={expanded}
                                    isHighlighted={
                                        highlightedScopeClauseId ===
                                        scopeClause.id
                                    }
                                    onPageClick={() =>
                                        onClauseSelect?.(scopeClause.id)
                                    }
                                />
                            ))}
                        </Stack>
                    </Stack>
                </Box>
            </Stack>
        </AccordionDetails>
    );
};
